import React from 'react';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { ChartSimpleIcon, CircleInfoIcon } from '@revfluence/fresh-icons/solid/esm';
import type { PostPerformanceSectionProps } from './hooks';

export const PostPerformanceSection: React.FC<PostPerformanceSectionProps> = ({
  isLoading,
  data
}) => {
  if (isLoading) {
    return (
      <div className="flex flex-col gap-3 overflow-hidden">
        <div className="flex items-center gap-2">
          <Skeleton className="h-6 w-6 rounded-lg" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-4 rounded-full" />
        </div>
        <div className="flex gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex-1 p-3 rounded-xl border border-grey-3">
              <Skeleton className="h-3 w-24 mb-2" />
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
        <div className="flex gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex-1 p-3 rounded-xl border border-grey-3">
              <Skeleton className="h-3 w-24 mb-2" />
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
        <div className="flex gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex-1 p-3 rounded-xl border border-grey-3">
              <Skeleton className="h-3 w-24 mb-2" />
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 overflow-hidden">
      <div className="flex flex-col">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-blue-5 rounded-lg flex justify-center items-center">
            <ChartSimpleIcon className="w-3 h-3 text-white" />
          </div>
          <div className="text-grey-6 text-sm font-medium leading-normal">Post Performance</div>
          <div className="flex items-start">
            <CircleInfoIcon className="w-4 h-4 text-grey-4" />
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        <div className="flex-1 p-3 rounded-xl border border-grey-3 flex flex-col">
          <div className="flex flex-col gap-2">
            <div className="flex justify-between items-center">
              <div className="flex-1 text-grey-6 text-xs font-medium leading-tight">Avg. Impressions per Post</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <div className="text-grey-5 text-base font-normal leading-normal">{data.avgImpressionsPerPost}</div>
          </div>
        </div>

        <div className="flex-1 p-3 rounded-xl border border-grey-3 flex flex-col">
          <div className="flex flex-col gap-2">
            <div className="flex justify-between items-center">
              <div className="flex-1 text-grey-6 text-xs font-medium leading-tight">Post Impression Ratio</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <div className="text-grey-5 text-base font-normal leading-normal">{data.postImpressionRatio}</div>
          </div>
        </div>

        <div className="flex-1 p-3 rounded-xl border border-grey-3 flex flex-col">
          <div className="flex flex-col gap-2">
            <div className="flex justify-between items-center">
              <div className="flex-1 text-grey-6 text-xs font-medium leading-tight">Stories Impression Ratio</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <div className="text-grey-5 text-base font-normal leading-normal">{data.storiesImpressionRatio}</div>
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        <div className="flex-1 p-3 rounded-xl border border-grey-3 flex flex-col">
          <div className="flex flex-col gap-2">
            <div className="flex justify-between items-center">
              <div className="flex-1 text-grey-6 text-xs font-medium leading-tight">Comment Sentiment</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <div className="text-grey-5 text-base font-normal leading-normal">{data.commentSentiment}</div>
          </div>
        </div>

        <div className="flex-1 p-3 rounded-xl border border-grey-3 flex flex-col">
          <div className="flex flex-col gap-2">
            <div className="flex justify-between items-center">
              <div className="flex-1 text-grey-6 text-xs font-medium leading-tight">Avg. Saves per Post</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <div className="text-grey-5 text-base font-normal leading-normal">{data.avgSavesPerPost}</div>
          </div>
        </div>

        <div className="flex-1 p-3 rounded-xl border border-grey-3 flex flex-col">
          <div className="flex flex-col gap-2">
            <div className="flex justify-between items-center">
              <div className="flex-1 text-grey-6 text-xs font-medium leading-tight">Likes per Post</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <div className="text-grey-5 text-base font-normal leading-normal">{data.likesPerPost}</div>
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        <div className="flex-1 p-3 rounded-xl border border-grey-3 flex flex-col">
          <div className="flex flex-col gap-2">
            <div className="flex justify-between items-center">
              <div className="flex-1 text-grey-6 text-xs font-medium leading-tight">Avg. Comments per Post</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <div className="text-grey-5 text-base font-normal leading-normal">{data.avgCommentsPerPost}</div>
          </div>
        </div>

        <div className="flex-1 p-3 rounded-xl border border-grey-3 flex flex-col">
          <div className="flex flex-col gap-2">
            <div className="flex justify-between items-center">
              <div className="flex-1 text-grey-6 text-xs font-medium leading-tight">Likes to Comments Ratio</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <div className="text-grey-5 text-base font-normal leading-normal">{data.likesToCommentsRatio}</div>
          </div>
        </div>
      </div>
    </div>
  );
};
